import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EvidenceBadge from "@/components/EvidenceBadge";
import DevilsAdvocateBlock from "@/components/DevilsAdvocateBlock";
import FormulaChat from "@/components/formula/FormulaChat";
import CompetitorsTab from "@/components/formula/CompetitorsTab";
import EconomicsTab from "@/components/formula/EconomicsTab";
import VersionsTab from "@/components/formula/VersionsTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import {
    ArrowLeft, CheckCircle, FilePdf, Warning, Globe, Pill, ShieldCheck, CurrencyEur,
    Sparkle, GitBranch, ChatsCircle, ClockCounterClockwise, Scales, CalendarCheck, Skull,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TAB_CLS = "rounded-none border-b-2 border-transparent bg-transparent px-3 py-2.5 text-sm text-[var(--ink-2)] data-[state=active]:border-[var(--sage)] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[var(--ink)] inline-flex items-center gap-1.5";

export default function FormulaDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const [f, setF] = useState(null);
    const [pricing, setPricing] = useState(false);

    useEffect(() => {
        api.get(`/formulas/${id}`).then((r) => setF(r.data)).catch(() => setF(null));
    }, [id]);

    // Poll while the formula is being generated by Claude (max ~3 min)
    useEffect(() => {
        if (!f || f.status !== "generating") return;
        let tries = 0;
        const t = setInterval(async () => {
            tries += 1;
            try {
                const r = await api.get(`/formulas/${id}`);
                if (r.data?.status !== "generating") {
                    setF(r.data);
                    clearInterval(t);
                    if (r.data?.status === "error") {
                        toast.error("La generación falló — revisa el detalle");
                    } else {
                        toast.success("Fórmula lista");
                    }
                }
            } catch {}
            if (tries > 36) clearInterval(t);
        }, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [f?.id, f?.status]);

    // Poll pricing while it's being computed in background (max ~120s)
    useEffect(() => {
        if (!f || f.pricing || f.feasibility === "rejected" || f.status === "generating") return;
        let tries = 0;
        const t = setInterval(async () => {
            tries += 1;
            try {
                const r = await api.get(`/formulas/${id}`);
                if (r.data?.pricing) {
                    setF(r.data);
                    clearInterval(t);
                }
            } catch {}
            if (tries > 24) clearInterval(t);  // ~120s
        }, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [f?.id, f?.pricing]);

    const approve = async () => {
        await api.post(`/formulas/${id}/approve`);
        toast.success("Fórmula aprobada");
        setF((p) => ({ ...p, status: "approved" }));
    };

    const addToPortfolio = async () => {
        try {
            await api.post("/portfolio", {
                name: f.title,
                indication: f.objective,
                composition: f.components.map((c) => ({ slug: c.compound_slug, name: c.compound_name, dose_mg: c.dose_mg })),
                source_formula_id: f.id,
                notes: f.synergy_rationale,
            });
            toast.success("Añadido al portfolio");
        } catch {
            toast.error("No se pudo añadir al portfolio");
        }
    };

    const computePricing = async () => {
        setPricing(true);
        try {
            const { data } = await api.post(`/formulas/${id}/pricing`);
            setF((p) => ({ ...p, pricing: data }));
            toast.success("Análisis económico completado");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error en el análisis económico");
        } finally { setPricing(false); }
    };

    if (!f) return <div className="p-12"><span className="label-eyebrow">cargando…</span></div>;

    // Generating state: friendly placeholder + polling indicator
    if (f.status === "generating") {
        return (
            <div className="fade-up">
                <PageHeader
                    eyebrow="Fórmula · generando…"
                    title={f.title}
                    lead="Claude está diseñando la fórmula, consultando evidencia y comprobando seguridad. Tardará entre 30 y 90 segundos."
                />
                <div className="px-8 md:px-12 py-12 max-w-2xl" data-testid="formula-generating">
                    <div className="border hairline bg-white p-8 flex items-center gap-4">
                        <Sparkle size={28} className="text-[var(--sage)] animate-pulse" weight="duotone"/>
                        <div>
                            <div className="font-display text-xl tracking-tight">Diseño en curso</div>
                            <p className="text-sm text-[var(--ink-2)] mt-1">Esta página se actualizará automáticamente cuando termine.</p>
                        </div>
                    </div>
                    <Link to="/formulas" className="mt-6 inline-flex items-center gap-1 text-sm text-[var(--ink-2)] hover:text-[var(--ink)]"><ArrowLeft size={14}/> Volver al listado</Link>
                </div>
            </div>
        );
    }

    if (f.status === "error") {
        return (
            <div className="fade-up">
                <PageHeader
                    eyebrow="Fórmula · ERROR de generación"
                    title="No se pudo completar la fórmula"
                    lead={f.generation_error
                        ? `Detalle del error: "${f.generation_error}"`
                        : "Claude no devolvió una respuesta válida. Reintenta — los retries automáticos suelen resolverlo."}
                />
                <div className="px-8 md:px-12 py-8 max-w-2xl space-y-3" data-testid="formula-error">
                    <div className="flex items-center gap-3 flex-wrap">
                        <Link to="/formulas/nuevo" className="btn-primary inline-flex items-center gap-2">Reintentar generación</Link>
                        <button
                            onClick={async () => {
                                await api.delete(`/formulas/${id}`);
                                toast.success("Movida a la papelera");
                                navigate("/formulas");
                            }}
                            className="px-4 py-2.5 border hairline bg-white text-sm hover:bg-[var(--cream)] text-[var(--terra-2)] inline-flex items-center gap-2"
                            data-testid="formula-error-trash"
                        >
                            Mover a la papelera
                        </button>
                        <Link to="/formulas" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">
                            ← Volver al listado
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const pdfUrl = `${API_BASE}/pdf/formula/${f.id}?token=${encodeURIComponent(token() || "")}`;
    const directCount = (f.competitors_ranked || []).filter((c) => c.tier === "direct").length;

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={`Fórmula · ${f.objective}${f.aesthetic_treatment ? ` · ${f.aesthetic_treatment}` : ""}${f.version > 1 ? ` · v${f.version}` : ""}${f.feasibility === "rejected" ? " · RECHAZADA" : ""}`}
                title={f.title}
                lead={f.synergy_rationale}
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        {f.feasibility !== "rejected" && (
                            <button
                                onClick={() => {
                                    const slugs = (f.components || []).map((c) => c.compound_slug).filter(Boolean).join(",");
                                    const qs = new URLSearchParams();
                                    qs.set("parent", f.id);
                                    if (slugs) qs.set("preferred", slugs);
                                    if (f.patient_id) qs.set("patient", f.patient_id);
                                    navigate(`/formulas/nuevo?${qs.toString()}`);
                                }}
                                className="px-4 py-2.5 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2"
                                data-testid="formula-detail-new-version-button"
                            >
                                <GitBranch size={16}/> Nueva versión
                            </button>
                        )}
                        {f.feasibility !== "rejected" && (
                            <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="px-4 py-2.5 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2"
                                data-testid="formula-detail-pdf-button"
                            >
                                <FilePdf size={16} /> Descargar PDF
                            </a>
                        )}
                        {f.feasibility !== "rejected" && f.status !== "approved" ? (
                            <button onClick={approve} className="btn-primary inline-flex items-center gap-2" data-testid="formula-detail-approve-button">
                                <CheckCircle size={16} weight="bold" /> Aprobar
                            </button>
                        ) : f.feasibility !== "rejected" ? (
                            <button onClick={addToPortfolio} className="btn-primary inline-flex items-center gap-2" data-testid="formula-detail-add-portfolio-button">
                                Añadir al portfolio
                            </button>
                        ) : null}
                    </div>
                }
            >
                <div className="mt-4 flex items-center gap-4 flex-wrap">
                    <Link to="/formulas" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]">
                        <ArrowLeft size={14} /> Volver a fórmulas
                    </Link>
                    {f.parent_formula_id && (
                        <Link to={`/formulas/${f.parent_formula_id}`} className="text-xs mono text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--sage)]" data-testid="formula-parent-link">
                            <GitBranch size={12}/> Versión basada en fórmula previa
                        </Link>
                    )}
                    {f.change_summary && (
                        <span className="text-xs text-[var(--ink-2)] border-l-2 border-[var(--sage)] pl-2" data-testid="formula-change-summary">{f.change_summary}</span>
                    )}
                </div>
            </PageHeader>

            <div className="px-8 md:px-12 py-8 max-w-6xl">
                <Tabs defaultValue="composicion">
                    <TabsList className="h-auto w-full justify-start flex-wrap gap-0 bg-transparent p-0 border-b hairline rounded-none mb-8">
                        <TabsTrigger value="composicion" className={TAB_CLS} data-testid="tab-composicion"><Pill size={14}/> Composición</TabsTrigger>
                        <TabsTrigger value="dialogo" className={TAB_CLS} data-testid="tab-dialogo"><ChatsCircle size={14}/> Diálogo IA</TabsTrigger>
                        <TabsTrigger value="devil" className={TAB_CLS} data-testid="tab-devil"><Skull size={14}/> Abogado del diablo</TabsTrigger>
                        <TabsTrigger value="diferenciacion" className={TAB_CLS} data-testid="tab-diferenciacion"><Scales size={14}/> Diferenciación</TabsTrigger>
                        <TabsTrigger value="competidores" className={TAB_CLS} data-testid="tab-competidores">
                            <Globe size={14}/> Competidores{directCount > 0 ? ` (${directCount})` : ""}
                        </TabsTrigger>
                        <TabsTrigger value="posologia" className={TAB_CLS} data-testid="tab-posologia"><CalendarCheck size={14}/> Posología</TabsTrigger>
                        <TabsTrigger value="economia" className={TAB_CLS} data-testid="tab-economia"><CurrencyEur size={14}/> Economía</TabsTrigger>
                        <TabsTrigger value="historial" className={TAB_CLS} data-testid="tab-historial"><ClockCounterClockwise size={14}/> Historial{f.version > 1 ? ` (v${f.version})` : ""}</TabsTrigger>
                    </TabsList>

                    {/* ---------------- Composición íntegra + justificación ---------------- */}
                    <TabsContent value="composicion" className="mt-0">
                        <div className="grid lg:grid-cols-[2fr_1fr] gap-10">
                            <div className="space-y-8">
                                {f.feasibility === "rejected" && (
                                    <section className="border hairline bg-[var(--cream)] p-6" data-testid="formula-rejection-block">
                                        <div className="flex items-center gap-2 label-eyebrow text-[var(--terra-2)] mb-3">
                                            <Warning size={16} weight="duotone"/> Combinación RECHAZADA
                                        </div>
                                        <p className="text-sm mb-3 text-[var(--ink)]">Esta combinación no se considera segura o sinérgicamente recomendable.</p>
                                        <ul className="text-sm space-y-1.5">
                                            {(f.rejection_reasons || []).map((r, i) => <li key={i}>• {r}</li>)}
                                        </ul>
                                    </section>
                                )}

                                {f.patient_snapshot && (
                                    <section className="border hairline bg-white p-5" data-testid="formula-patient-block">
                                        <div className="label-eyebrow mb-2">Paciente asociado</div>
                                        <Link to={`/pacientes/${f.patient_snapshot.id}`} className="font-display text-xl tracking-tight hover:text-[var(--sage)]">
                                            {f.patient_snapshot.name}
                                        </Link>
                                        <div className="text-xs text-[var(--ink-2)] mt-1 mono">
                                            {[f.patient_snapshot.code, f.patient_snapshot.sex, f.patient_snapshot.dob].filter(Boolean).join(" · ")}
                                        </div>
                                    </section>
                                )}

                                <section data-testid="formula-components-section">
                                    <div className="label-eyebrow mb-4 flex items-center gap-2"><Pill size={14}/> Composición íntegra y justificación</div>
                                    <div className="space-y-4">
                                        {f.components.map((c, idx) => {
                                            const forced = c.inclusion_type === "forced";
                                            const improvement = c.inclusion_type === "improvement";
                                            const cardCls = forced
                                                ? "border-[var(--terra-2)] bg-[#FBEAE9]"
                                                : improvement
                                                ? "border-[var(--sage)] bg-[#E8F1E9]"
                                                : "hairline bg-white";
                                            return (
                                            <div key={idx} className={`border p-5 ${cardCls}`} data-testid={`component-${c.compound_slug}`}>
                                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <Link to={`/compuestos/${c.compound_slug}`} className="font-display text-2xl tracking-tight hover:text-[var(--sage)]">{c.compound_name}</Link>
                                                            {forced && (
                                                                <span className="citation-badge bg-[var(--terra-2)] text-white border-[var(--terra-2)] inline-flex items-center gap-1" data-testid={`component-forced-${c.compound_slug}`}>
                                                                    <Warning size={11}/> FORZADO · no óptimo
                                                                </span>
                                                            )}
                                                            {improvement && (
                                                                <span className="citation-badge bg-[var(--sage)] text-white border-[var(--sage)] inline-flex items-center gap-1" data-testid={`component-improvement-${c.compound_slug}`}>
                                                                    <CheckCircle size={11}/> MEJORA VALIDADA
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-1 mono text-sm text-[var(--ink-2)]">{c.dose_mg} {c.unit} · {c.frequency}</div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {(c.evidence || []).map((e, i) => (
                                                            <EvidenceBadge key={i} source={e.source} externalId={e.external_id} url={e.url} label={e.title}/>
                                                        ))}
                                                    </div>
                                                </div>
                                                <p className="text-sm mt-3 text-[var(--ink)] leading-relaxed">{c.rationale}</p>
                                            </div>
                                        );})}
                                    </div>
                                </section>
                            </div>

                            <aside className="space-y-5">
                                <div className="border hairline bg-white p-5">
                                    <div className="label-eyebrow mb-3">Estado</div>
                                    <div className={`citation-badge ${f.status === "approved" ? "bg-[var(--sage)] text-white border-[var(--sage)]" : ""}`}>{f.status === "approved" ? "Aprobada" : "Borrador"}</div>
                                </div>

                                {f.warnings?.length > 0 && (
                                    <div className="border hairline bg-[var(--cream)] p-5">
                                        <div className="flex items-center gap-2 label-eyebrow mb-3 text-[var(--terra-2)]"><Warning size={14} weight="duotone"/> Advertencias</div>
                                        <ul className="text-sm space-y-1.5">
                                            {f.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                                        </ul>
                                    </div>
                                )}

                                {f.contraindications?.length > 0 && (
                                    <div className="border hairline bg-white p-5">
                                        <div className="label-eyebrow mb-3">Contraindicaciones</div>
                                        <ul className="text-sm space-y-1.5 text-[var(--ink-2)]">
                                            {f.contraindications.map((c, i) => <li key={i}>• {c}</li>)}
                                        </ul>
                                    </div>
                                )}

                                {f.regulatory_compliance && (
                                    <div className="border hairline bg-white p-5">
                                        <div className="label-eyebrow mb-3 flex items-center gap-2"><ShieldCheck size={14}/> Normativa</div>
                                        <p className="text-xs text-[var(--ink-2)] mb-2 mono">{f.regulatory_compliance.eu_status}</p>
                                        {f.regulatory_compliance.notes && <p className="text-xs text-[var(--ink-2)]">{f.regulatory_compliance.notes}</p>}
                                    </div>
                                )}
                            </aside>
                        </div>
                    </TabsContent>

                    {/* ---------------- Diálogo IA ---------------- */}
                    <TabsContent value="dialogo" className="mt-0">
                        <FormulaChat formula={f} />
                    </TabsContent>

                    {/* ---------------- Abogado del diablo ---------------- */}
                    <TabsContent value="devil" className="mt-0">
                        <div className="max-w-3xl">
                            <DevilsAdvocateBlock
                                devilsAdvocate={f.devils_advocate}
                                expectedComplications={f.expected_complications}
                                populationsWhereMightFail={f.populations_where_might_fail}
                                testid="formula-devils-advocate"
                            />
                        </div>
                    </TabsContent>

                    {/* ---------------- Diferenciación ---------------- */}
                    <TabsContent value="diferenciacion" className="mt-0">
                        <div className="max-w-3xl space-y-6">
                            {f.differentiation_notes ? (
                                <section className="border hairline bg-white p-6" data-testid="formula-differentiation-block">
                                    <div className="label-eyebrow mb-3 flex items-center gap-2"><Scales size={13}/> Qué nos diferencia del mercado</div>
                                    <p className="text-sm leading-relaxed">{f.differentiation_notes}</p>
                                </section>
                            ) : (
                                <div className="border hairline bg-white p-6 text-sm text-[var(--ink-3)]">Sin notas de diferenciación registradas.</div>
                            )}
                            {f.primary_indication && (
                                <section className="border hairline bg-white p-6">
                                    <div className="label-eyebrow mb-2">Indicación principal</div>
                                    <p className="text-sm">{f.primary_indication}</p>
                                </section>
                            )}
                            {(f.alternative_indications || []).length > 0 && (
                                <section className="border hairline bg-white p-6">
                                    <div className="label-eyebrow mb-3">Indicaciones alternativas</div>
                                    <ul className="text-sm space-y-1.5">
                                        {f.alternative_indications.map((a, i) => <li key={i}>• {a}</li>)}
                                    </ul>
                                </section>
                            )}
                            {(f.applicable_treatments || []).length > 0 && (
                                <section className="border hairline bg-white p-6">
                                    <div className="label-eyebrow mb-3">Tratamientos estéticos aplicables</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {f.applicable_treatments.map((t, i) => <span key={i} className="citation-badge">{t}</span>)}
                                    </div>
                                </section>
                            )}
                        </div>
                    </TabsContent>

                    {/* ---------------- Competidores ranking ---------------- */}
                    <TabsContent value="competidores" className="mt-0">
                        <CompetitorsTab formula={f} onUpdated={(data) => setF(data)} />
                    </TabsContent>

                    {/* ---------------- Posología ---------------- */}
                    <TabsContent value="posologia" className="mt-0">
                        <div className="max-w-3xl" data-testid="formula-posology-section">
                            {(f.posology || f.schedule) ? (
                                <div className="border hairline bg-white p-6">
                                    <div className="label-eyebrow mb-3">Dosificación del producto formulado</div>
                                    {f.posology && <p className="mb-4 text-sm leading-relaxed">{f.posology}</p>}
                                    {f.schedule && (
                                        <div className="grid sm:grid-cols-3 gap-4 mt-4">
                                            {["daily", "weekly", "monthly"].map((k) => (
                                                f.schedule[k] && (
                                                    <div key={k} className="bg-[var(--cream)] p-4 border hairline">
                                                        <div className="label-eyebrow mb-1">{ {daily: "Diario", weekly: "Semanal", monthly: "Mensual"}[k] }</div>
                                                        <div className="text-sm">{f.schedule[k]}</div>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="border hairline bg-white p-6 text-sm text-[var(--ink-3)]">Sin pauta posológica registrada.</div>
                            )}
                        </div>
                    </TabsContent>

                    {/* ---------------- Economía ---------------- */}
                    <TabsContent value="economia" className="mt-0">
                        <EconomicsTab formula={f} computePricing={computePricing} busy={pricing} />
                    </TabsContent>

                    {/* ---------------- Historial de versiones ---------------- */}
                    <TabsContent value="historial" className="mt-0">
                        <VersionsTab formulaId={f.id} />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
